import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  SwapHoriz as TransferIcon,
  Folder as SpaceIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Space } from '@risk-management/shared';
import { spacesService } from '../services/spaces.service';

interface TransferComponentDialogProps {
  open: boolean;
  onClose: () => void;
  onTransfer: (targetSpaceId: string) => Promise<void>;
  componentName: string;
  currentSpaceId: string;
  isLoading?: boolean;
}

export const TransferComponentDialog: React.FC<
  TransferComponentDialogProps
> = ({
  open,
  onClose,
  onTransfer,
  componentName,
  currentSpaceId,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch accessible spaces
  const { data: spaces = [], isLoading: spacesLoading } = useQuery({
    queryKey: ['spaces', 'accessible'],
    queryFn: spacesService.getAccessible,
    enabled: open,
  });

  // Filter out current space
  const availableSpaces = spaces.filter(s => s.id !== currentSpaceId);

  const handleTransfer = async () => {
    if (!selectedSpace) return;

    setError(null);
    try {
      await onTransfer(selectedSpace.id);
      setSelectedSpace(null);
      onClose();
    } catch (err) {
      setError((err as Error).message || t('component.transfer.error'));
    }
  };

  const handleClose = () => {
    setSelectedSpace(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TransferIcon color="primary" />
          {t('component.transfer.title')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('component.transfer.description', { name: componentName })}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Autocomplete
          options={availableSpaces}
          getOptionLabel={option => option.name}
          value={selectedSpace}
          onChange={(_, newValue) => setSelectedSpace(newValue)}
          loading={spacesLoading}
          renderInput={params => (
            <TextField
              {...params}
              label={t('component.transfer.selectSpace')}
              placeholder={t('component.transfer.searchSpaces')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {spacesLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                <SpaceIcon sx={{ mr: 1, color: 'action.active' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1">{option.name}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
                {option.isPublic && (
                  <Chip
                    label={t('space.public')}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            );
          }}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          noOptionsText={t('component.transfer.noSpaces')}
        />

        {selectedSpace && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('component.transfer.warning', {
              component: componentName,
              space: selectedSpace.name,
            })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          color="primary"
          disabled={!selectedSpace || isLoading}
          startIcon={
            isLoading ? <CircularProgress size={20} /> : <TransferIcon />
          }
        >
          {t('component.transfer.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
